import React, { useEffect, useState } from 'react';
import { Github, Instagram, Twitter, Linkedin, ChevronDown, Sparkles, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { profileData, socialLinks } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const socialIcons = {
  github: Github,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin
};

const Hero = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-dark">
      {/* Animated background blobs */}
      <div className="blob w-96 h-96 bg-purple-600 top-20 -left-48" />
      <div className="blob w-80 h-80 bg-violet-500 bottom-20 -right-40" style={{ animationDelay: '2s' }} />
      <div className="blob w-64 h-64 bg-fuchsia-600 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ animationDelay: '4s' }} />
      
      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(147, 51, 234, 0.3) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(147, 51, 234, 0.3) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Status badge */}
        <div 
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-8 opacity-0 ${isVisible ? 'animate-fadeInUp' : ''}`}
        >
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-sm text-purple-300 font-medium">{t('hero.status')}</span>
        </div>

        {/* Name */}
        <h1 
          className={`text-5xl md:text-7xl lg:text-8xl font-bold mb-6 opacity-0 ${isVisible ? 'animate-fadeInUp animation-delay-200' : ''}`}
        >
          <span className="text-white">{t('hero.greeting')} </span>
          <span className="gradient-text text-glow">Ömer</span>
        </h1>

        {/* Title & Role */}
        <div 
          className={`flex flex-col items-center gap-2 mb-6 opacity-0 ${isVisible ? 'animate-fadeInUp animation-delay-400' : ''}`}
        >
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <span className="text-xl md:text-2xl text-purple-300 font-mono">{profileData.title}</span>
            <Sparkles className="w-5 h-5 text-purple-400" />
          </div>
          <p className="text-gray-400 text-lg">
            {profileData.role} @ <span className="text-purple-400">{profileData.companies[0]}</span> & <span className="text-purple-400">{profileData.companies[1]}</span>
          </p>
        </div>

        {/* Tagline */}
        <p 
          className={`text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed opacity-0 ${isVisible ? 'animate-fadeInUp animation-delay-600' : ''}`}
        >
          {t('hero.tagline')}
        </p>

        {/* Social links + Email */}
        <div 
          className={`flex items-center justify-center gap-4 mb-16 opacity-0 ${isVisible ? 'animate-fadeInUp animation-delay-600' : ''}`}
          style={{ animationDelay: '0.8s' }}
        >
          {socialLinks.map((social) => {
            const Icon = socialIcons[social.icon];
            return (
              <a
                key={social.id}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-purple-500/50 hover:bg-purple-500/10 transition-all duration-300"
                aria-label={social.name}
              >
                <Icon className="w-6 h-6 text-gray-400 group-hover:text-purple-400 transition-colors duration-300" />
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  {social.username}
                </span>
              </a>
            );
          })}
          {/* Email button */}
          <a
            href={`mailto:${profileData.email}`}
            className="group relative p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-purple-500/50 hover:bg-purple-500/10 transition-all duration-300"
            aria-label="Email"
          >
            <Mail className="w-6 h-6 text-gray-400 group-hover:text-purple-400 transition-colors duration-300" />
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              {profileData.email}
            </span>
          </a>
        </div>

        {/* CTA Buttons */}
        <div 
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 ${isVisible ? 'animate-fadeInUp' : ''}`}
          style={{ animationDelay: '1s' }}
        >
          <Button 
            onClick={() => scrollToSection('projects')}
            className="px-8 py-6 text-lg bg-purple-600 hover:bg-purple-500 text-white rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
          >
            {t('hero.projectsBtn')}
          </Button>
          <Button 
            variant="outline"
            onClick={() => scrollToSection('contact')}
            className="px-8 py-6 text-lg border-purple-500/50 text-purple-300 hover:bg-purple-500/10 hover:text-white rounded-xl transition-all duration-300"
          >
            {t('hero.contactBtn')}
          </Button>
        </div>

        {/* Scroll indicator */}
        <div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer"
          onClick={() => scrollToSection('about')}
        >
          <ChevronDown className="w-8 h-8 text-purple-400 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
